<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="7">
            <h1 class="h4 mb-1 font-weight-bold">Products and services</h1>
            <p class="text-muted mb-0">What you sell, ready to add to quotations and invoices</p>
          </CCol>
          <CCol md="5" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              :disabled="loading"
              @click="refreshTable"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add product
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard>
      <CCardBody>
        <!-- First load -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading</span>
          </div>
          <p class="mt-3 text-muted mb-0">Loading products…</p>
        </div>

        <!-- Load failed -->
        <div v-else-if="error && !items.length" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="text-danger mb-3" />
          <h2 class="h5">Couldn't load products</h2>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- First use -->
        <div v-else-if="!items.length" class="text-center py-5">
          <CIcon name="cil-tags" size="2xl" class="text-muted mb-3" />
          <h2 class="h5">No products or services yet</h2>
          <p class="text-muted mb-3">
            Add what you sell once, then pick it on quotations and invoices.
          </p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add product
          </CButton>
        </div>

        <template v-else>
          <CRow class="mb-3">
            <CCol md="6" lg="4">
              <CInput
                v-model="search"
                label="Search"
                placeholder="Name, description or account"
                class="mb-0"
              >
                <template #prepend-content><CIcon name="cil-magnifying-glass" /></template>
              </CInput>
            </CCol>
            <CCol class="d-flex align-items-end justify-content-md-end mt-2 mt-md-0">
              <small class="text-muted">
                {{ filteredItems.length }} of {{ items.length }} products
              </small>
            </CCol>
          </CRow>

          <!-- Filtered empty -->
          <div v-if="!filteredItems.length" class="text-center py-4">
            <h2 class="h6">No products match your search</h2>
            <CButton color="light" class="mt-2" @click="search = ''">
              <CIcon name="cil-x" /> Clear search
            </CButton>
          </div>

          <template v-else>
            <!-- Desktop table -->
            <div class="d-none d-lg-block">
              <CDataTable
                :items="filteredItems"
                :fields="fields"
                :items-per-page="25"
                :loading="loading"
                hover
                sorter
                pagination
              >
                <template #name="{ item }">
                  <td class="py-2 font-weight-bold">{{ item.name || "Unnamed product" }}</td>
                </template>
                <template #description="{ item }">
                  <td class="py-2">
                    <span v-if="item.description">{{ item.description }}</span>
                    <span v-else class="text-muted">—</span>
                  </td>
                </template>
                <template #chartOfAccountName="{ item }">
                  <td class="py-2">
                    <span v-if="item.chartOfAccountName">{{ item.chartOfAccountName }}</span>
                    <span v-else class="text-muted">Not set</span>
                  </td>
                </template>
                <template #actions="{ item }">
                  <td class="py-2 text-right text-nowrap">
                    <CButton
                      color="light"
                      size="sm"
                      class="mr-1"
                      :aria-label="`Edit ${item.name || 'product'}`"
                      @click="onEdit(item)"
                    >
                      <CIcon name="cil-pencil" />
                    </CButton>
                    <CButton
                      color="light"
                      size="sm"
                      class="text-danger"
                      :aria-label="`Delete ${item.name || 'product'}`"
                      @click="showDeleteConfirmation(item)"
                    >
                      <CIcon name="cil-trash" />
                    </CButton>
                  </td>
                </template>
              </CDataTable>
            </div>

            <!-- Mobile card list -->
            <div class="d-lg-none">
              <CCard v-for="item in filteredItems" :key="item.id" class="mb-2">
                <CCardBody class="p-3">
                  <div class="font-weight-bold">{{ item.name || "Unnamed product" }}</div>
                  <div v-if="item.description" class="small text-muted">{{ item.description }}</div>
                  <div v-if="item.chartOfAccountName" class="small">
                    Account: {{ item.chartOfAccountName }}
                  </div>
                  <div class="d-flex justify-content-between mt-3">
                    <CButton color="light" class="text-danger" @click="showDeleteConfirmation(item)">
                      <CIcon name="cil-trash" /> Delete
                    </CButton>
                    <CButton color="primary" variant="outline" @click="onEdit(item)">
                      <CIcon name="cil-pencil" /> Edit
                    </CButton>
                  </div>
                </CCardBody>
              </CCard>
            </div>
          </template>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete confirmation -->
    <CModal
      :title="`Delete ${itemToDelete.name || 'this product'}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        {{ itemToDelete.name || "This product" }} will be removed from your products and services.
        This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <span v-if="deleting" class="spinner-border spinner-border-sm mr-1" role="status" aria-hidden="true"></span>
          Delete product
        </CButton>
      </template>
    </CModal>

    <WidgetEditProductModal
      :show.sync="showProductModal"
      :obj="selectedProduct"
      @save="handleProductSave"
      @error="handleProductError"
    />
  </div>
</template>

<script>
import ProductApi from "@/lib/productApi";
import WidgetEditProductModal from "../widgets/WidgetEditProductModal.vue";

const fields = [
  { key: "name", label: "Name" },
  { key: "description", label: "Description" },
  { key: "chartOfAccountName", label: "Account" },
  { key: "actions", label: "", sorter: false, _style: "width:1%" },
];

export default {
  name: "ProductList",
  components: {
    WidgetEditProductModal,
  },
  data() {
    return {
      showProductModal: false,
      selectedProduct: {}, // Empty = new product
      loading: true,
      error: false,
      items: [],
      fields,
      search: "",
      api: new ProductApi(),
      warningModal: false,
      deleting: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Sales", "Products/Services"]);
    this.refreshTable();
  },
  computed: {
    computedItems() {
      return this.items.map((item) => ({
        ...item,
        chartOfAccountName: this.getChartOfAccountName(item),
      }));
    },
    filteredItems() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.computedItems;
      return this.computedItems.filter((p) =>
        [p.name, p.description, p.chartOfAccountName]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      );
    },
  },
  methods: {
    getChartOfAccountName(item) {
      return (item.chartAccount && item.chartAccount.name) || "";
    },
    refreshTable() {
      var self = this;
      self.loading = true;
      self.error = false;
      self.api
        .getListByCurrentBusiness()
        .then((response) => {
          self.items = response.result || [];
          self.loading = false;
        })
        .catch((err) => {
          console.error("Load products failed", err);
          self.loading = false;
          self.error = true;
          if (self.items.length) {
            self.$toast.error("Couldn't refresh products. Check your connection and try again.");
          }
        });
    },
    onEdit(item) {
      this.selectedProduct = item;
      this.showProductModal = true;
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      var self = this;
      const name = self.itemToDelete.name || "the product";
      self.deleting = true;
      self.api
        .delete(self.itemToDelete.id)
        .then(() => {
          self.deleting = false;
          self.warningModal = false;
          self.itemToDelete = {};
          self.$toast.success("Product deleted");
          self.refreshTable();
        })
        .catch(({ data } = {}) => {
          self.deleting = false;
          self.$toast.error(
            `Couldn't delete ${name}. ` + helper.getErrorMessage(data, "Please try again.")
          );
        });
    },
    addNew() {
      this.selectedProduct = {};
      this.showProductModal = true;
    },
    handleProductSave() {
      const editing = !!(this.selectedProduct && this.selectedProduct.id);
      this.showProductModal = false;
      this.$toast.success(editing ? "Product saved" : "Product added");
      this.refreshTable();
    },
    handleProductError(message) {
      this.$toast.error(message || "Couldn't save the product. Please try again.");
    },
  },
};
</script>
